import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Users,
  Mail,
  MapPin,
  Globe,
  Instagram,
  Twitter,
  ExternalLink,
  ArrowLeft,
  Calendar,
  Image,
  ToggleLeft,
  ToggleRight,
  Send,
  RefreshCw,
  Check,
  Star,
  FileStack,
  LogOut,
  Link as LinkIcon,
  Camera,
  Upload,
  Trash2,
  X,
  Plus,
  Pencil,
  Save,
  ChevronLeft,
  ChevronRight,
  Coins,
  Eye,
  TrendingUp,
  BarChart3
} from "lucide-react";
import { compressImage, compressImageToBase64, compressionPresets } from "../utils/imageCompression";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

// Helper to get correct image data URI
const getImageSrc = (base64Data) => {
  if (!base64Data) return null;
  if (base64Data.startsWith("data:")) return base64Data;
  
  if (base64Data.startsWith("UklGR")) {
    return `data:image/webp;base64,${base64Data}`;
  } else if (base64Data.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${base64Data}`;
  } else if (base64Data.startsWith("iVBOR")) {
    return `data:image/png;base64,${base64Data}`;
  }
  return `data:image/jpeg;base64,${base64Data}`;
};

// TikTok icon component
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    className={className}
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const AdminCreatorProfilePage = () => {
  const { creatorId } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [togglingLanding, setTogglingLanding] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const [resettingCredentials, setResettingCredentials] = useState(false);
  // Content management state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadCategory, setUploadCategory] = useState("free");
  const [uploadCost, setUploadCost] = useState(0);
  const [customCost, setCustomCost] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingContentId, setDeletingContentId] = useState(null);
  const fileInputRef = useRef(null);
  // Edit profile state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editProfileImage, setEditProfileImage] = useState(null);
  const [editProfilePreview, setEditProfilePreview] = useState(null);
  const [editHeaderImage, setEditHeaderImage] = useState(null);
  const [editHeaderPreview, setEditHeaderPreview] = useState(null);
  const editProfileInputRef = useRef(null);
  const editHeaderInputRef = useRef(null);
  // Edit content state
  const [showEditContentModal, setShowEditContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [editContentForm, setEditContentForm] = useState({ title: "", description: "", category: "free", cost: 0 });
  const [savingContent, setSavingContent] = useState(false);
  
  // Cost preset values
  const costPresets = [0, 10, 25, 50, 100, 200];
  
  // Lightbox carousel state
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  
  // Image lazy loading state
  const [imageCache, setImageCache] = useState({});
  const [loadingImages, setLoadingImages] = useState({});

  // Page view stats state
  const [pageStats, setPageStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Credits editing state
  const [editingCredits, setEditingCredits] = useState(false);
  const [editCreditsValue, setEditCreditsValue] = useState("");
  const [savingCredits, setSavingCredits] = useState(false);

  // Featured toggle state
  const [togglingFeatured, setTogglingFeatured] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== "admin") {
      navigate("/admin");
      return;
    }
    
    setCurrentUser(userData);
    fetchCreator();
  }, [creatorId, navigate]);

  // Lightbox navigation handlers
  const content = creator?.content || [];
  const totalImages = content.length;

  const goToPrevious = useCallback(() => {
    if (selectedImageIndex !== null && totalImages > 0) {
      const newIndex = selectedImageIndex === 0 ? totalImages - 1 : selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      // Preload the new image if not cached
      const contentId = content[newIndex]?.id;
      if (contentId && !imageCache[contentId]) {
        loadImage(contentId);
      }
    }
  }, [selectedImageIndex, totalImages, content, imageCache]);

  const goToNext = useCallback(() => {
    if (selectedImageIndex !== null && totalImages > 0) {
      const newIndex = selectedImageIndex === totalImages - 1 ? 0 : selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      // Preload the new image if not cached
      const contentId = content[newIndex]?.id;
      if (contentId && !imageCache[contentId]) {
        loadImage(contentId);
      }
    }
  }, [selectedImageIndex, totalImages, content, imageCache]);

  const closeLightbox = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNext();
          break;
        case "Escape":
          e.preventDefault();
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex, goToPrevious, goToNext, closeLightbox]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (selectedImageIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedImageIndex]);

  const fetchCreator = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/creators/${creatorId}`, {
        headers: {}
      });
      setCreator(response.data);
      // Also fetch page stats
      fetchPageStats();
    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/admin/users");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch page view statistics
  const fetchPageStats = async () => {
    setLoadingStats(true);
    try {
      
      const response = await axios.get(`${API}/admin/creators/${creatorId}/page-stats`, {
        headers: {}
      });
      setPageStats(response.data);
    } catch (err) {
      console.error("Failed to fetch page stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  // Lazy load a single image - using useCallback to avoid stale closure issues
  const loadImage = useCallback(async (contentId) => {
    if (!contentId) return;
    
    // Check if already cached or loading using refs to avoid stale closure
    setImageCache(prev => {
      if (prev[contentId]) return prev; // Already cached
      
      setLoadingImages(loadingPrev => {
        if (loadingPrev[contentId]) return loadingPrev; // Already loading
        
        // Start loading
        const newLoadingState = { ...loadingPrev, [contentId]: true };
        
        // Fetch the image
        
        axios.get(
          `${API}/admin/creators/${creatorId}/content/${contentId}/image`,
          { headers: {} }
        ).then(response => {
          if (response.data.image_data) {
            setImageCache(cachePrev => ({ ...cachePrev, [contentId]: response.data.image_data }));
          }
        }).catch(err => {
          console.error("Failed to load image:", err);
        }).finally(() => {
          setLoadingImages(loadingPrev2 => {
            const { [contentId]: _, ...rest } = loadingPrev2;
            return rest;
          });
        });
        
        return newLoadingState;
      });
      
      return prev;
    });
  }, [creatorId]);

  // Load visible images using Intersection Observer
  const imageObserverRef = useRef(null);
  
  useEffect(() => {
    imageObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const contentId = entry.target.dataset.contentId;
            if (contentId) {
              loadImage(contentId);
            }
          }
        });
      },
      { rootMargin: "100px" }
    );

    return () => {
      if (imageObserverRef.current) {
        imageObserverRef.current.disconnect();
      }
    };
  }, [creatorId, loadImage]);

  // Observe content items when creator data loads
  useEffect(() => {
    if (creator?.content && imageObserverRef.current) {
      const contentItems = document.querySelectorAll('[data-content-id]');
      contentItems.forEach((item) => {
        imageObserverRef.current.observe(item);
      });
    }
  }, [creator?.content]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
    localStorage.removeItem("user");
    navigate("/login");
  };

  const toggleLandingPage = async () => {
    setTogglingLanding(true);
    try {
      
      await axios.patch(
        `${API}/admin/creators/${creatorId}/landing-page`,
        { landing_page_active: !creator.landing_page_active },
        { headers: {} }
      );
      setCreator(prev => ({ ...prev, landing_page_active: !prev.landing_page_active }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to toggle landing page");
    } finally {
      setTogglingLanding(false);
    }
  };

  // Toggle featured status
  const toggleFeatured = async () => {
    setTogglingFeatured(true);
    try {
      await axios.patch(
        `${API}/admin/creators/${creatorId}/landing-page`,
        { is_featured: !creator.is_featured },
        { headers: {} }
      );
      setCreator(prev => ({ ...prev, is_featured: !prev.is_featured }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to toggle featured status");
    } finally {
      setTogglingFeatured(false);
    }
  };

  // Update creator credits
  const updateCredits = async () => {
    const credits = parseInt(editCreditsValue);
    if (isNaN(credits) || credits < 0) {
      alert("Please enter a valid non-negative number");
      return;
    }
    
    setSavingCredits(true);
    try {
      
      await axios.patch(
        `${API}/admin/users/${creatorId}/credits`,
        { credits },
        { headers: {} }
      );
      setCreator(prev => ({ ...prev, credits }));
      setEditingCredits(false);
      setEditCreditsValue("");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update credits");
    } finally {
      setSavingCredits(false);
    }
  };

  const sendCredentials = async () => {
    if (!window.confirm(`Send login credentials to ${creator.email}?`)) return;
    
    setSendingCredentials(true);
    try {
      
      const response = await axios.post(
        `${API}/admin/creators/${creatorId}/send-credentials`,
        {},
        { headers: {} }
      );
      alert(response.data.message);
      setCreator(prev => ({ ...prev, credentials_sent: true }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send credentials");
    } finally {
      setSendingCredentials(false);
    }
  };

  const resetCredentials = async () => {
    if (!window.confirm("Reset this creator's password?")) return;
    
    setResettingCredentials(true);
    try {
      
      const response = await axios.post(
        `${API}/admin/creators/${creatorId}/reset-credentials`,
        {},
        { headers: {} }
      );
      alert(response.data.message);
      setCreator(prev => ({ ...prev, credentials_sent: false }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reset credentials");
    } finally {
      setResettingCredentials(false);
    }
  };

  // Content Management Functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      
      if (!isImage && !isVideo) {
        alert("Please select an image or video file");
        return;
      }
      
      // Different size limits: 100MB for videos, 10MB for images
      const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File too large. Maximum size is ${isVideo ? '100MB' : '10MB'}`);
        return;
      }
      
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setUploadPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadTitle("");
    setUploadDescription("");
    setUploadCategory("free");
    setUploadCost(0);
    setCustomCost("");
  };

  const handleUploadContent = async () => {
    if (!uploadFile) {
      alert("Please select a file to upload");
      return;
    }
    
    setUploading(true);
    try {
      // Only compress images, not videos
      const isVideo = uploadFile.type.startsWith("video/");
      const fileToUpload = isVideo ? uploadFile : await compressImage(uploadFile, compressionPresets.content);
      
      const formData = new FormData();
      formData.append("file", fileToUpload);
      if (uploadTitle) formData.append("title", uploadTitle);
      if (uploadDescription) formData.append("description", uploadDescription);
      formData.append("category", uploadCategory);
      formData.append("cost", uploadCost.toString());
      
      await axios.post(
        `${API}/admin/users/${creatorId}/content`,
        formData,
        { 
          headers: { 
            
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      
      // Refresh creator data to get updated content
      await fetchCreator();
      closeUploadModal();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to upload content");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;
    
    setDeletingContentId(contentId);
    try {
      
      await axios.delete(
        `${API}/admin/users/${creatorId}/content/${contentId}`,
        { headers: {} }
      );
      
      // Update local state
      setCreator(prev => ({
        ...prev,
        content: prev.content.filter(c => c.id !== contentId)
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete content");
    } finally {
      setDeletingContentId(null);
    }
  };

  // Edit Content Functions
  const openEditContentModal = (content) => {
    setEditingContent(content);
    setEditContentForm({
      title: content.title || "",
      description: content.description || "",
      category: content.category || "free",
      cost: content.cost || 0
    });
    setShowEditContentModal(true);
  };

  const closeEditContentModal = () => {
    setShowEditContentModal(false);
    setEditingContent(null);
    setEditContentForm({ title: "", description: "", category: "free", cost: 0 });
  };

  const handleSaveContent = async () => {
    if (!editingContent) return;
    
    setSavingContent(true);
    try {
      
      await axios.patch(
        `${API}/admin/users/${creatorId}/content/${editingContent.id}`,
        editContentForm,
        { headers: {} }
      );
      
      // Update local state
      setCreator(prev => ({
        ...prev,
        content: prev.content.map(c => 
          c.id === editingContent.id 
            ? { ...c, ...editContentForm }
            : c
        )
      }));
      closeEditContentModal();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update content");
    } finally {
      setSavingContent(false);
    }
  };

  // Edit Profile Functions
  const openEditModal = () => {
    setEditForm({
      name: creator.name || "",
      bio: creator.bio || "",
      location: creator.location || "",
      landing_page_slug: creator.landing_page_slug || "",
      landing_page_headline: creator.landing_page_headline || "",
      landing_page_description: creator.landing_page_description || "",
      show_platform_branding: creator.show_platform_branding !== false,
      show_hotpeek_link: creator.show_hotpeek_link === true,
      instagram: creator.instagram || "",
      twitter: creator.twitter || "",
      tiktok: creator.tiktok || "",
      onlyfans: creator.onlyfans || "",
      website: creator.website || ""
    });
    setEditProfilePreview(creator.profile_image ? getImageSrc(creator.profile_image) : null);
    setEditProfileImage(null);
    setEditHeaderPreview(creator.header_image ? getImageSrc(creator.header_image) : null);
    setEditHeaderImage(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditForm({});
    setEditProfileImage(null);
    setEditProfilePreview(null);
    setEditHeaderImage(null);
    setEditHeaderPreview(null);
  };

  const handleEditProfileImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size is 5MB");
        return;
      }
      setEditProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setEditProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEditHeaderImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size is 5MB");
        return;
      }
      setEditHeaderImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setEditHeaderPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      
      
      // Prepare update data
      const updateData = { ...editForm };
      
      // If there's a new profile image, compress and convert to base64
      if (editProfileImage) {
        updateData.profile_image = await compressImageToBase64(editProfileImage, compressionPresets.avatar);
      }
      
      // If there's a new header image, compress and convert to base64
      if (editHeaderImage) {
        updateData.header_image = await compressImageToBase64(editHeaderImage, compressionPresets.header);
      }
      
      await axios.patch(
        `${API}/admin/creators/${creatorId}/landing-page`,
        updateData,
        { headers: {} }
      );
      
      // Refresh creator data
      await fetchCreator();
      closeEditModal();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-gray-500">Creator not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-creator-profile">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">HotPeek</span>
              </h1>
              <nav className="flex items-center gap-1">
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-applications"
                >
                  <FileStack size={18} />
                  <span className="hidden sm:inline">Applications</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-2 px-3 py-2 text-white bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-users"
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">Users</span>
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{currentUser?.name}</p>
                <p className="text-gray-500 text-sm capitalize">{currentUser?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/admin/users")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Users
        </button>

        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden mb-6"
        >
          {/* Header Image */}
          <div className="h-40 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 relative">
            {creator.header_image && (
              <img
                src={getImageSrc(creator.header_image)}
                alt="Header"
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
              {/* Avatar */}
              <div className="relative">
                {creator.avatar || creator.profile_image ? (
                  <img
                    src={getImageSrc(creator.avatar || creator.profile_image)}
                    alt={creator.name}
                    className="w-24 h-24 rounded-xl object-cover border-4 border-gray-800 bg-gray-700"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-xl border-4 border-gray-800 bg-gray-700 flex items-center justify-center">
                    <User size={40} className="text-gray-500" />
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                  Creator
                </div>
              </div>

              {/* Name & Info */}
              <div className="flex-1 pt-4 sm:pt-0">
                <h2 className="text-2xl font-bold text-white">{creator.name}</h2>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mt-1">
                  <span className="flex items-center gap-1">
                    <Mail size={14} />
                    {creator.email}
                  </span>
                  {creator.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {creator.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    Joined {formatDate(creator.created_at)}
                  </span>
                  {/* Editable Credits */}
                  {editingCredits ? (
                    <div className="flex items-center gap-2">
                      <Coins size={14} className="text-amber-400" />
                      <input
                        type="number"
                        min="0"
                        value={editCreditsValue}
                        onChange={(e) => setEditCreditsValue(e.target.value)}
                        className="w-24 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-amber-500"
                        placeholder="Credits"
                        autoFocus
                      />
                      <button
                        onClick={updateCredits}
                        disabled={savingCredits}
                        className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50"
                        title="Save"
                      >
                        {savingCredits ? (
                          <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                        ) : (
                          <Check size={16} />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setEditingCredits(false);
                          setEditCreditsValue("");
                        }}
                        className="p-1 text-gray-400 hover:bg-gray-600 rounded transition-colors"
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <span 
                      className="flex items-center gap-1 text-amber-400 font-medium cursor-pointer hover:text-amber-300 transition-colors"
                      onClick={() => {
                        setEditingCredits(true);
                        setEditCreditsValue(creator.credits?.toString() || "0");
                      }}
                      title="Click to edit credits"
                    >
                      <Coins size={14} />
                      {creator.credits || 0} credits
                      <Pencil size={12} className="opacity-50" />
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Edit Profile Button */}
                <button
                  onClick={openEditModal}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                  data-testid="edit-profile-btn"
                >
                  <Pencil size={16} />
                  Edit Profile
                </button>
                
                {/* Credentials */}
                {!creator.credentials_sent ? (
                  <button
                    onClick={sendCredentials}
                    disabled={sendingCredentials}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                    data-testid="send-credentials-btn"
                  >
                    {sendingCredentials ? (
                      <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    Send Credentials
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/30">
                      <Check size={12} />
                      Sent
                    </span>
                    <button
                      onClick={resetCredentials}
                      disabled={resettingCredentials}
                      className="p-2 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Reset password"
                    >
                      {resettingCredentials ? (
                        <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      ) : (
                        <RefreshCw size={16} />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            {creator.bio && (
              <p className="text-gray-300 mt-4">{creator.bio}</p>
            )}

            {/* Social Links */}
            <div className="flex flex-wrap gap-2 mt-4">
              {creator.instagram && (
                <a
                  href={creator.instagram.startsWith("http") ? creator.instagram : `https://instagram.com/${creator.instagram.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <Instagram size={16} />
                  Instagram
                  <ExternalLink size={12} className="text-gray-500" />
                </a>
              )}
              {creator.twitter && (
                <a
                  href={creator.twitter.startsWith("http") ? creator.twitter : `https://twitter.com/${creator.twitter.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <Twitter size={16} />
                  Twitter/X
                  <ExternalLink size={12} className="text-gray-500" />
                </a>
              )}
              {creator.tiktok && (
                <a
                  href={creator.tiktok.startsWith("http") ? creator.tiktok : `https://tiktok.com/@${creator.tiktok.replace("@", "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <TikTokIcon size={16} />
                  TikTok
                  <ExternalLink size={12} className="text-gray-500" />
                </a>
              )}
              {creator.onlyfans && (
                <a
                  href={creator.onlyfans.startsWith("http") ? creator.onlyfans : `https://onlyfans.com/${creator.onlyfans}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <Star size={16} />
                  OnlyFans
                  <ExternalLink size={12} className="text-gray-500" />
                </a>
              )}
              {creator.website && (
                <a
                  href={creator.website.startsWith("http") ? creator.website : `https://${creator.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <Globe size={16} />
                  Website
                  <ExternalLink size={12} className="text-gray-500" />
                </a>
              )}
            </div>
          </div>
        </motion.div>

        {/* Landing Page Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Globe size={20} className="text-cyan-400" />
              Landing Page
            </h3>
            <button
              onClick={toggleLandingPage}
              disabled={togglingLanding}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                creator.landing_page_active
                  ? "bg-green-500/20 text-green-400 border border-green-500/30"
                  : "bg-gray-700/50 text-gray-400 border border-gray-600/30"
              }`}
            >
              {togglingLanding ? (
                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              ) : creator.landing_page_active ? (
                <ToggleRight size={18} />
              ) : (
                <ToggleLeft size={18} />
              )}
              {creator.landing_page_active ? "Active" : "Inactive"}
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-3">
              <LinkIcon size={16} className="text-gray-500" />
              <code className="text-cyan-400 flex-1">/c/{creator.landing_page_slug}</code>
              {creator.landing_page_active && (
                <a
                  href={`/c/${creator.landing_page_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-cyan-400 transition-colors"
                  title="Open landing page"
                >
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
            {/* Public Profile Link */}
            <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-4 py-3">
              <User size={16} className="text-gray-500" />
              <code className="text-purple-400 flex-1">/u/{creator.landing_page_slug}</code>
              {creator.landing_page_active && (
                <a
                  href={`/u/${creator.landing_page_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1 text-gray-400 hover:text-purple-400 transition-colors"
                  title="Open public profile"
                  data-testid="open-public-profile-link"
                >
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
            {creator.landing_page_headline && (
              <p className="text-gray-400 text-sm">
                <span className="text-gray-500">Headline:</span> {creator.landing_page_headline}
              </p>
            )}
          </div>
        </motion.div>

        {/* Featured Creator Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Star size={20} className="text-amber-400" />
                Featured Creator
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Featured creators are promoted on fan profile pages
              </p>
            </div>
            <button
              onClick={toggleFeatured}
              disabled={togglingFeatured}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                creator.is_featured
                  ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  : "bg-gray-700/50 text-gray-400 border border-gray-600/30"
              }`}
              data-testid="toggle-featured-btn"
            >
              {togglingFeatured ? (
                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              ) : creator.is_featured ? (
                <ToggleRight size={18} />
              ) : (
                <ToggleLeft size={18} />
              )}
              {creator.is_featured ? "Featured" : "Not Featured"}
            </button>
          </div>
        </motion.div>

        {/* Page View Statistics Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 size={20} className="text-green-400" />
              Page View Statistics
            </h3>
            <button
              onClick={fetchPageStats}
              disabled={loadingStats}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {loadingStats ? (
                <div className="w-4 h-4 border-2 border-gray-500 border-t-white rounded-full animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Refresh
            </button>
          </div>

          {loadingStats && !pageStats ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-gray-600 border-t-cyan-400 rounded-full animate-spin" />
            </div>
          ) : pageStats ? (
            <div className="space-y-6">
              {/* Landing Page Stats */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <Globe size={16} className="text-cyan-400" />
                  Landing Page (/c/{creator.landing_page_slug})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">Total Views</p>
                    <p className="text-xl font-bold text-white">{pageStats.landing_page?.total?.views || 0}</p>
                    <p className="text-xs text-cyan-400">{pageStats.landing_page?.total?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">Today</p>
                    <p className="text-xl font-bold text-white">{pageStats.landing_page?.today?.views || 0}</p>
                    <p className="text-xs text-green-400">{pageStats.landing_page?.today?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Week</p>
                    <p className="text-xl font-bold text-white">{pageStats.landing_page?.this_week?.views || 0}</p>
                    <p className="text-xs text-blue-400">{pageStats.landing_page?.this_week?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Month</p>
                    <p className="text-xl font-bold text-white">{pageStats.landing_page?.this_month?.views || 0}</p>
                    <p className="text-xs text-purple-400">{pageStats.landing_page?.this_month?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Year</p>
                    <p className="text-xl font-bold text-white">{pageStats.landing_page?.this_year?.views || 0}</p>
                    <p className="text-xs text-amber-400">{pageStats.landing_page?.this_year?.unique || 0} unique</p>
                  </div>
                </div>
              </div>

              {/* Profile Page Stats */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                  <User size={16} className="text-purple-400" />
                  Profile Page (/u/{creator.name})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">Total Views</p>
                    <p className="text-xl font-bold text-white">{pageStats.profile_page?.total?.views || 0}</p>
                    <p className="text-xs text-cyan-400">{pageStats.profile_page?.total?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">Today</p>
                    <p className="text-xl font-bold text-white">{pageStats.profile_page?.today?.views || 0}</p>
                    <p className="text-xs text-green-400">{pageStats.profile_page?.today?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Week</p>
                    <p className="text-xl font-bold text-white">{pageStats.profile_page?.this_week?.views || 0}</p>
                    <p className="text-xs text-blue-400">{pageStats.profile_page?.this_week?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Month</p>
                    <p className="text-xl font-bold text-white">{pageStats.profile_page?.this_month?.views || 0}</p>
                    <p className="text-xs text-purple-400">{pageStats.profile_page?.this_month?.unique || 0} unique</p>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600/30">
                    <p className="text-xs text-gray-500 mb-1">This Year</p>
                    <p className="text-xl font-bold text-white">{pageStats.profile_page?.this_year?.views || 0}</p>
                    <p className="text-xs text-amber-400">{pageStats.profile_page?.this_year?.unique || 0} unique</p>
                  </div>
                </div>
              </div>

              {/* Daily Chart Preview */}
              {(pageStats.landing_page?.daily?.length > 0 || pageStats.profile_page?.daily?.length > 0) && (
                <div className="mt-4 pt-4 border-t border-gray-700/50">
                  <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                    <TrendingUp size={16} className="text-green-400" />
                    Last 30 Days Activity
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Landing Page Mini Chart */}
                    <div className="bg-gray-700/20 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-2">Landing Page</p>
                      <div className="flex items-end gap-0.5 h-16">
                        {(pageStats.landing_page?.daily || []).slice(-14).map((day) => (
                          <div
                            key={day.date}
                            className="flex-1 bg-cyan-500/60 rounded-t hover:bg-cyan-500 transition-colors"
                            style={{ height: `${Math.max(4, (day.views / Math.max(...(pageStats.landing_page?.daily || []).map(d => d.views), 1)) * 100)}%` }}
                            title={`${day.date}: ${day.views} views, ${day.unique} unique`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Last 14 days</p>
                    </div>
                    {/* Profile Page Mini Chart */}
                    <div className="bg-gray-700/20 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-2">Profile Page</p>
                      <div className="flex items-end gap-0.5 h-16">
                        {(pageStats.profile_page?.daily || []).slice(-14).map((day) => (
                          <div
                            key={day.date}
                            className="flex-1 bg-purple-500/60 rounded-t hover:bg-purple-500 transition-colors"
                            style={{ height: `${Math.max(4, (day.views / Math.max(...(pageStats.profile_page?.daily || []).map(d => d.views), 1)) * 100)}%` }}
                            title={`${day.date}: ${day.views} views, ${day.unique} unique`}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Last 14 days</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Eye size={32} className="mx-auto text-gray-600 mb-3" />
              <p className="text-gray-500">No page view data available yet</p>
              <p className="text-gray-600 text-sm mt-1">Views will be tracked when visitors access the pages</p>
            </div>
          )}
        </motion.div>

        {/* Content Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Image size={20} className="text-purple-400" />
              Content ({creator.content?.length || 0} images)
            </h3>
            <button
              onClick={openUploadModal}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
              data-testid="upload-content-btn"
            >
              <Upload size={16} />
              Upload Content
            </button>
          </div>

          {creator.content && creator.content.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {creator.content.map((item, index) => (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: Math.min(index * 0.05, 0.3) }}
                  className="group relative aspect-square rounded-xl overflow-hidden bg-gray-700/50 border border-gray-600/30 cursor-pointer"
                  data-testid={`content-item-${item.id}`}
                  data-content-id={item.id}
                  onClick={() => {
                    // Load image if not cached before opening lightbox
                    if (!imageCache[item.id]) {
                      loadImage(item.id);
                    }
                    setSelectedImageIndex(index);
                  }}
                >
                  {/* Show loading placeholder or cached image/video */}
                  {imageCache[item.id] ? (
                    item.media_type === "video" || imageCache[item.id]?.startsWith("data:video/") ? (
                      <video
                        src={imageCache[item.id]}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        onMouseOver={(e) => e.target.play()}
                        onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      />
                    ) : (
                      <img
                        src={getImageSrc(imageCache[item.id])}
                        alt={item.title || `Content ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-700">
                      {loadingImages[item.id] ? (
                        <div className="w-8 h-8 border-2 border-gray-500 border-t-cyan-400 rounded-full animate-spin" />
                      ) : (
                        <Image size={32} className="text-gray-500" />
                      )}
                    </div>
                  )}
                  {/* Badges - Top Left (Video, Category, Cost) */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
                    {/* Video indicator */}
                    {(item.media_type === "video" || imageCache[item.id]?.startsWith("data:video/")) && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/90 text-white flex items-center gap-1">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        Video
                      </span>
                    )}
                    {/* Category badge */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      item.category === "premium" 
                        ? "bg-amber-500/90 text-black" 
                        : "bg-green-500/90 text-white"
                    }`}>
                      {item.category === "premium" ? "Premium" : "Free"}
                    </span>
                    {/* Cost badge */}
                    {item.cost > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/90 text-white flex items-center gap-1">
                        <Coins size={10} />
                        {item.cost}
                      </span>
                    )}
                  </div>
                  {/* Title - Bottom */}
                  {item.title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <p className="text-white text-xs font-medium truncate">{item.title}</p>
                    </div>
                  )}
                  {/* Hover overlay with view, edit and delete buttons */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!imageCache[item.id]) {
                          loadImage(item.id);
                        }
                        setSelectedImageIndex(index);
                      }}
                      className="p-3 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors"
                      title="View full size"
                    >
                      <Image size={20} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditContentModal(item);
                      }}
                      className="p-3 bg-blue-500/80 hover:bg-blue-500 text-white rounded-full transition-colors"
                      title="Edit content"
                      data-testid={`edit-content-${item.id}`}
                    >
                      <Pencil size={20} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteContent(item.id);
                      }}
                      disabled={deletingContentId === item.id}
                      className="p-3 bg-red-500/80 hover:bg-red-500 text-white rounded-full transition-colors disabled:opacity-50"
                      title="Delete content"
                      data-testid={`delete-content-${item.id}`}
                    >
                      {deletingContentId === item.id ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={20} />
                      )}
                    </button>
                  </div>
                  {/* Title overlay */}
                  {item.title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <p className="text-white text-xs truncate">{item.title}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Camera size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-500">No content uploaded yet</p>
              <p className="text-gray-600 text-sm mt-2">Click "Upload Content" to add images for this creator</p>
            </div>
          )}
        </motion.div>
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={closeUploadModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl my-8 flex flex-col max-h-[90vh]"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
                <h3 className="text-xl font-semibold text-white">Upload Content</h3>
                <button
                  onClick={closeUploadModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* File Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                    uploadPreview 
                      ? "border-[#00A8E8]/50 bg-[#00A8E8]/5" 
                      : "border-gray-600 hover:border-gray-500 hover:bg-gray-700/30"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {uploadPreview ? (
                    <div className="space-y-3">
                      {uploadFile?.type?.startsWith("video/") ? (
                        <video
                          src={uploadPreview}
                          className="max-h-48 mx-auto rounded-lg object-contain"
                          controls
                          muted
                        />
                      ) : (
                        <img
                          src={uploadPreview}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg object-contain"
                        />
                      )}
                      <p className="text-sm text-gray-400">Click to change {uploadFile?.type?.startsWith("video/") ? "video" : "image"}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 mx-auto rounded-full bg-gray-700 flex items-center justify-center">
                        <Plus size={32} className="text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Click to select an image or video</p>
                        <p className="text-sm text-gray-500">Images: JPEG, PNG, WebP, GIF up to 10MB</p>
                        <p className="text-sm text-gray-500">Videos: MP4, WebM, MOV up to 100MB</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Enter a title for this content"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8] transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setUploadCategory("free")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadCategory("premium")}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        uploadCategory === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Free content is visible to registered fans. Premium requires payment.
                  </p>
                </div>

                {/* Cost Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <Coins size={16} className="text-amber-400" />
                      Credit Cost
                    </div>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {costPresets.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => {
                          setUploadCost(preset);
                          setCustomCost("");
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          uploadCost === preset && customCost === ""
                            ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                            : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        {preset === 0 ? "Free" : preset}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Custom:</span>
                    <input
                      type="number"
                      min="0"
                      value={customCost}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomCost(val);
                        if (val !== "") {
                          setUploadCost(parseInt(val) || 0);
                        }
                      }}
                      placeholder="Enter custom credits"
                      className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Set the credit cost to unlock this content. Use 0 for free content.
                  </p>
                </div>

                {/* Upload for user info */}
                <div className="bg-gray-700/30 rounded-lg p-3">
                  <p className="text-sm text-gray-400">
                    Uploading content for: <span className="text-white font-medium">{creator.name}</span>
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeUploadModal}
                    className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadContent}
                    disabled={!uploadFile || uploading}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Upload size={18} />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={closeEditModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-2xl border border-gray-700 shadow-2xl my-8"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Pencil size={20} className="text-blue-400" />
                  Edit Creator Profile
                </h3>
                <button
                  onClick={closeEditModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Header Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Header Image</label>
                  <div
                    onClick={() => editHeaderInputRef.current?.click()}
                    className="w-full h-32 rounded-xl overflow-hidden bg-gray-700 border-2 border-dashed border-gray-600 hover:border-gray-500 cursor-pointer transition-colors flex items-center justify-center"
                  >
                    {editHeaderPreview ? (
                      <img src={editHeaderPreview} alt="Header" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        <Image size={32} className="text-gray-500 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">Click to add header image</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={editHeaderInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleEditHeaderImageSelect}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 mt-2">Recommended: 1200x300px. JPEG, PNG, WebP up to 5MB</p>
                </div>

                {/* Profile Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Profile Image</label>
                  <div className="flex items-center gap-4">
                    <div
                      onClick={() => editProfileInputRef.current?.click()}
                      className="w-24 h-24 rounded-xl overflow-hidden bg-gray-700 border-2 border-dashed border-gray-600 hover:border-gray-500 cursor-pointer transition-colors flex items-center justify-center"
                    >
                      {editProfilePreview ? (
                        <img src={editProfilePreview} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <Camera size={32} className="text-gray-500" />
                      )}
                    </div>
                    <input
                      ref={editProfileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleEditProfileImageSelect}
                      className="hidden"
                    />
                    <div className="text-sm text-gray-400">
                      <p>Click to change profile image</p>
                      <p className="text-xs text-gray-500">JPEG, PNG, WebP up to 5MB</p>
                    </div>
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={editForm.name || ""}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Display name"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                    <input
                      type="text"
                      value={editForm.location || ""}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, Country"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bio</label>
                  <textarea
                    value={editForm.bio || ""}
                    onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about this creator..."
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                {/* Landing Page Settings */}
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Globe size={18} className="text-cyan-400" />
                    Landing Page Settings
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Username / Slug</label>
                      <div className="flex items-center">
                        <span className="px-3 py-3 bg-gray-700 border border-r-0 border-gray-600 rounded-l-lg text-gray-400 text-sm">/c/</span>
                        <input
                          type="text"
                          value={editForm.landing_page_slug || ""}
                          onChange={(e) => setEditForm(prev => ({ ...prev, landing_page_slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                          placeholder="username"
                          className="flex-1 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-r-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Letters, numbers, underscores, hyphens only</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Headline</label>
                      <input
                        type="text"
                        value={editForm.landing_page_headline || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, landing_page_headline: e.target.value }))}
                        placeholder="Follow me for exclusive content!"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Landing Page Description</label>
                    <textarea
                      value={editForm.landing_page_description || ""}
                      onChange={(e) => setEditForm(prev => ({ ...prev, landing_page_description: e.target.value }))}
                      placeholder="Short description for the landing page..."
                      rows={2}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none"
                    />
                  </div>

                  {/* Platform Branding Toggle */}
                  <div className="mt-4 flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Show "Powered by HotPeek"</label>
                      <p className="text-xs text-gray-500 mt-1">Display platform branding at the bottom of the landing page</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm(prev => ({ ...prev, show_platform_branding: !prev.show_platform_branding }))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        editForm.show_platform_branding !== false ? 'bg-cyan-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          editForm.show_platform_branding !== false ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* HotPeek Profile Link Toggle */}
                  <div className="mt-4 flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Show HotPeek Profile Link</label>
                      <p className="text-xs text-gray-500 mt-1">Add a "HotPeek Profile" button to the landing page links</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm(prev => ({ ...prev, show_hotpeek_link: !prev.show_hotpeek_link }))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        editForm.show_hotpeek_link ? 'bg-cyan-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                          editForm.show_hotpeek_link ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Social Links */}
                <div className="pt-4 border-t border-gray-700">
                  <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <LinkIcon size={18} className="text-purple-400" />
                    Social Links
                  </h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                        <span className="text-pink-400">OnlyFans</span>
                      </label>
                      <input
                        type="text"
                        value={editForm.onlyfans || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, onlyfans: e.target.value }))}
                        placeholder="onlyfans.com/username or username"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                        <Instagram size={14} className="text-pink-400" />
                        Instagram
                      </label>
                      <input
                        type="text"
                        value={editForm.instagram || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, instagram: e.target.value }))}
                        placeholder="@username"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                        <Twitter size={14} className="text-blue-400" />
                        Twitter / X
                      </label>
                      <input
                        type="text"
                        value={editForm.twitter || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, twitter: e.target.value }))}
                        placeholder="@username"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">TikTok</label>
                      <input
                        type="text"
                        value={editForm.tiktok || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, tiktok: e.target.value }))}
                        placeholder="@username"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                        <Globe size={14} className="text-gray-400" />
                        Website
                      </label>
                      <input
                        type="text"
                        value={editForm.website || ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://example.com"
                        className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:border-gray-500 transition-all"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 p-6 border-t border-gray-700">
                <button
                  onClick={closeEditModal}
                  className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Content Modal */}
      <AnimatePresence>
        {showEditContentModal && editingContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeEditContentModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Pencil size={20} className="text-blue-400" />
                  Edit Content
                </h3>
                <button
                  onClick={closeEditContentModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Preview */}
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-700/50">
                  <img
                    src={getImageSrc(editingContent.image_data)}
                    alt={editingContent.title || "Content"}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Title Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={editContentForm.title}
                    onChange={(e) => setEditContentForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter a title"
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                  />
                </div>

                {/* Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                  <textarea
                    value={editContentForm.description}
                    onChange={(e) => setEditContentForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter a description"
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all resize-none"
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditContentForm(prev => ({ ...prev, category: "free" }))}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "free"
                          ? "bg-green-500/20 text-green-400 border-2 border-green-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Free
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditContentForm(prev => ({ ...prev, category: "premium" }))}
                      className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                        editContentForm.category === "premium"
                          ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                          : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                      }`}
                    >
                      Premium
                    </button>
                  </div>
                </div>

                {/* Cost Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <Coins size={16} className="text-amber-400" />
                      Credit Cost
                    </div>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {costPresets.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setEditContentForm(prev => ({ ...prev, cost: preset }))}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          editContentForm.cost === preset
                            ? "bg-amber-500/20 text-amber-400 border-2 border-amber-500"
                            : "bg-gray-700/50 text-gray-400 border border-gray-600 hover:border-gray-500"
                        }`}
                      >
                        {preset === 0 ? "Free" : preset}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-sm">Custom:</span>
                    <input
                      type="number"
                      min="0"
                      value={!costPresets.includes(editContentForm.cost) ? editContentForm.cost : ""}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        setEditContentForm(prev => ({ ...prev, cost: val }));
                      }}
                      placeholder="Enter custom credits"
                      className="flex-1 px-3 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Set the credit cost to unlock this content.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={closeEditContentModal}
                    className="flex-1 py-3 border border-gray-600 text-gray-300 font-medium rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveContent}
                    disabled={savingContent}
                    className="flex-1 py-3 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingContent ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox Carousel */}
      <AnimatePresence>
        {selectedImageIndex !== null && content.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex flex-col"
            onClick={closeLightbox}
            data-testid="lightbox-carousel"
          >
            {/* Top Bar */}
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex items-center gap-4">
                <span className="text-lg font-medium">
                  {selectedImageIndex + 1} / {totalImages}
                </span>
                {content[selectedImageIndex]?.title && (
                  <span className="text-gray-400 hidden sm:inline">
                    {content[selectedImageIndex].title}
                  </span>
                )}
                {content[selectedImageIndex]?.category && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    content[selectedImageIndex].category === "premium" 
                      ? "bg-amber-500/90 text-black" 
                      : "bg-green-500/90 text-white"
                  }`}>
                    {content[selectedImageIndex].category === "premium" ? "Premium" : "Free"}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Main Image Area */}
            <div className="flex-1 flex items-center justify-center relative px-4 sm:px-16">
              {/* Previous Button */}
              {totalImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevious();
                  }}
                  className="absolute left-2 sm:left-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={28} className="text-white" />
                </button>
              )}

              {/* Image/Video */}
              <motion.div
                key={selectedImageIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex items-center justify-center px-4"
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const currentItem = content[selectedImageIndex];
                  const contentId = currentItem?.id;
                  const cachedMedia = contentId ? imageCache[contentId] : null;
                  const isVideo = currentItem?.media_type === "video" || cachedMedia?.startsWith("data:video/");
                  
                  if (cachedMedia) {
                    if (isVideo) {
                      return (
                        <video
                          src={cachedMedia}
                          className="max-w-[80vw] max-h-[65vh] object-contain rounded-lg shadow-2xl"
                          style={{ minWidth: '200px', minHeight: '200px' }}
                          controls
                          autoPlay
                          playsInline
                        />
                      );
                    } else {
                      const imageSrc = getImageSrc(cachedMedia);
                      return (
                        <img
                          src={imageSrc}
                          alt={currentItem?.title || `Image ${selectedImageIndex + 1}`}
                          className="max-w-[80vw] max-h-[65vh] object-contain rounded-lg shadow-2xl"
                          draggable={false}
                          style={{ minWidth: '200px', minHeight: '200px' }}
                        />
                      );
                    }
                  } else {
                    return (
                      <div className="w-64 h-64 flex items-center justify-center bg-gray-800 rounded-lg">
                        <div className="w-12 h-12 border-4 border-gray-600 border-t-cyan-400 rounded-full animate-spin" />
                      </div>
                    );
                  }
                })()}
              </motion.div>

              {/* Next Button */}
              {totalImages > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  className="absolute right-2 sm:right-4 p-2 sm:p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={28} className="text-white" />
                </button>
              )}
            </div>

            {/* Thumbnail Strip */}
            {totalImages > 1 && (
              <div className="p-4 overflow-x-auto">
                <div className="flex gap-2 justify-center">
                  {content.map((item, index) => {
                    const isVideo = item.media_type === "video" || imageCache[item.id]?.startsWith("data:video/");
                    return (
                      <button
                        key={item.id || index}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!imageCache[item.id]) {
                            loadImage(item.id);
                          }
                          setSelectedImageIndex(index);
                        }}
                        className={`relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                          index === selectedImageIndex
                            ? "border-[#00A8E8] ring-2 ring-[#00A8E8]/50"
                            : "border-transparent opacity-50 hover:opacity-100"
                        }`}
                        data-testid={`thumbnail-${index}`}
                      >
                        {imageCache[item.id] ? (
                          isVideo ? (
                            <video
                              src={imageCache[item.id]}
                              className="w-full h-full object-cover"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={getImageSrc(imageCache[item.id])}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                              draggable={false}
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-700">
                            <Image size={16} className="text-gray-500" />
                          </div>
                        )}
                        {/* Video indicator on thumbnail */}
                        {isVideo && (
                          <div className="absolute bottom-1 right-1 bg-black/70 p-1 rounded-full">
                            <svg className="w-2 h-2 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Keyboard Hint */}
            <div className="hidden sm:flex justify-center pb-4 text-gray-500 text-sm gap-4">
              <span>← → Navigate</span>
              <span>ESC Close</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCreatorProfilePage;
